import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";

/**
 * RING-TIER-03 · Feature 3 — Persistent Rings 1–3 progress rail.
 *
 * Renders three tiles side-by-side: cleared / active / locked, mirroring the
 * data model's `ring_progress` + per-ring scores. Never rendered on Ring 4
 * or beyond — this is a Tier 1 chrome element.
 *
 * State per tile:
 *   cleared → green outline, tick, "R{n} · {score}"
 *   active  → amber outline, "R{n} · active"
 *   locked  → slate outline, "R{n}"
 */
export default function TierOneRail({ idea, ideaId }) {
  const nav = useNavigate();
  if (!idea) return null;
  const current = idea.current_ring || 1;
  const tierStatus = idea.tier_status || {};

  const ringState = (n) => {
    const score = idea?.[`ring${n}_score`];
    if (score && score.score >= 80 && score.advance_ready) return "cleared";
    if (n === current && !tierStatus.tier1_complete) return "active";
    if (n < current) return "cleared";
    return "locked";
  };

  const tiles = [1, 2, 3].map((n) => {
    const state = ringState(n);
    const score = idea?.[`ring${n}_score`];
    const label =
      state === "cleared"
        ? `${score?.score ?? "—"}/100`
        : state === "active"
        ? "active"
        : "locked";
    const RING_TITLES = {
      1: "Concept & Viability",
      2: "Marketing & Financials",
      3: "Deep Trench Obstacles",
    };
    return { n, state, label, title: RING_TITLES[n] };
  });

  return (
    <div
      className="tier-one-rail flex items-stretch gap-2 mb-6"
      data-testid="tier-one-rail"
    >
      {tiles.map((t) => (
        <button
          key={t.n}
          type="button"
          disabled={t.state === "locked"}
          onClick={() => t.state !== "locked" && nav(`/ideas/${ideaId}`)}
          data-testid={`tier-one-rail-r${t.n}`}
          data-state={t.state}
          className={[
            "flex-1 text-left rounded-lg border px-3 py-2 transition",
            t.state === "cleared" && "border-emerald-600 bg-emerald-50 hover:bg-emerald-100 text-emerald-900",
            t.state === "active" && "border-amber-500 bg-amber-50 text-amber-900",
            t.state === "locked" && "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <div className="mono-caps text-[0.6rem] leading-tight">
            Ring {t.n} · {t.state}
          </div>
          <div className="text-sm font-semibold mt-1">{t.title}</div>
          <div className="text-xs mt-0.5 opacity-80">{t.label}</div>
        </button>
      ))}
      {tierStatus.tier1_complete ? (
        <Link
          to={`/ideas/${ideaId}/graduation`}
          data-testid="tier-one-rail-graduation-link"
          className="flex-1 rounded-lg border border-violet-500 bg-violet-50 hover:bg-violet-100 text-violet-900 px-3 py-2 transition"
        >
          <div className="mono-caps text-[0.6rem] leading-tight">Tier 1 · complete</div>
          <div className="text-sm font-semibold mt-1">Graduation →</div>
          <div className="text-xs mt-0.5 opacity-80">Continue to Tier 2</div>
        </Link>
      ) : null}
    </div>
  );
}

TierOneRail.propTypes = {
  idea: PropTypes.object,
  ideaId: PropTypes.string,
};
